import type { TeammateLicenseType } from "@repo/app-types/entities/teammate";
import { menuStyle } from "@repo/ui/Menu/menuStyle";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { selectStyle } from "@repo/ui/Select/style";
import { IconCheck, IconSelector } from "@tabler/icons-react";
import type { FC } from "react";
import { Button, ListBox, ListBoxItem, Select } from "react-aria-components";
import { teammateLicenseCopy } from "./teammateLicenses.js";

export interface TeammateLicenseCellProps {
  teammateName: string;
  licenseType: TeammateLicenseType;
  /** The tiers the company's contract sells, in catalog order. */
  options: readonly TeammateLicenseType[];
  isDisabled?: boolean;
  onChange: (licenseType: TeammateLicenseType) => void;
}

/**
 * Compact inline picker for the license a teammate is billed under. Changing
 * between tiers is reversible, so selecting an item applies immediately
 * instead of opening a confirmation modal.
 */
export const TeammateLicenseCell: FC<TeammateLicenseCellProps> = (props) => {
  const {
    teammateName,
    licenseType,
    options,
    isDisabled = false,
    onChange,
  } = props;
  const { label } = teammateLicenseCopy[licenseType];
  const style = selectStyle();

  return (
    <Select
      aria-label={`License for ${teammateName}`}
      isDisabled={isDisabled}
      value={licenseType}
      onChange={(key) => {
        const selectedLicenseType = options.find((option) => option === key);
        if (selectedLicenseType && selectedLicenseType !== licenseType) {
          onChange(selectedLicenseType);
        }
      }}
    >
      <Button className={style.button()}>
        <span className={style.value()}>{label}</span>
        <IconSelector aria-hidden className={style.chevron()} />
      </Button>

      <SimpleMenuPopover placement="bottom end" className="w-88">
        <ListBox
          aria-label={`Choose a license for ${teammateName}`}
          items={options.map((option) => ({
            id: option,
            ...teammateLicenseCopy[option],
          }))}
          disallowEmptySelection
        >
          {(option) => (
            <ListBoxItem
              id={option.id}
              textValue={option.label}
              className={menuStyle().item({
                className: "items-stretch px-3 py-2.5",
              })}
            >
              <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5">
                <span className="text-sm font-medium text-foreground">
                  {option.label}
                </span>
                <IconCheck
                  aria-hidden
                  className={menuStyle().itemIcon({
                    className:
                      "invisible text-indigo-600 group-selected:visible",
                  })}
                />
                <span className="col-span-2 text-xs leading-4 font-normal text-muted-foreground">
                  {option.capability}
                </span>
              </span>
            </ListBoxItem>
          )}
        </ListBox>
      </SimpleMenuPopover>
    </Select>
  );
};
